import { Alert } from '@design-system/components/alert/alert';
import { Button } from '@design-system/components/button/button';
import { Col, Row } from '@design-system/components/grid/grid';
import { SideModal } from '@design-system/components/modal/sideModal';
import { Typography } from '@design-system/components/typography/typography';
import { Switch } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons';
import { EditPlatformBundleVM } from '../../viewModel/editPlatformBundle.vm';
import {
  messageWrapper,
  promoteWrapper,
  wrapper,
  ctaWrapper,
  limitWrapper,
  freeTrialSection,
  rowSection,
  rowContent,
  rowActions,
  headerRow,
} from './PlatformBundleModal.css';
import { Divider } from '@mui/material';
import { ExternalLink } from '@design-system/components/link/externalLink';

type PlatformBundleModalProps = EditPlatformBundleVM['platformBundleModal'] & {
  isOpen: boolean;
  close: () => void;
};

export const PlatformBundleModal = ({
  isOpen,
  close,
  price,
  freeTrial,
  save,
  submitting,
  hasExclusiveSub,
  promotions,
  landingPage,
  ...p
}: PlatformBundleModalProps) => {
  return (
    <SideModal
      isOpen={isOpen}
      onClose={close}
      noSpacing
      testId="platformBundleModal"
    >
      <div className={wrapper}>
        <div className={headerRow}>
          <Typography variant="headline" size="small">
            Platform users
          </Typography>
          {!hasExclusiveSub && (
            <Button
              color="neutral"
              size="medium"
              onClick={save}
              loading={submitting}
            >
              Save
            </Button>
          )}
        </div>
        <Price monthly={price.monthly} annual={price.annual} />
        <Divider />
        <Alert
          variant="info"
          title="What are platform users?"
          customComponent={
            <div className="inline">
              <Typography variant="span" size="medium" fontWeight="normal">
                Some users who use Playbook Platform still have access to your
                subscription based on the old pricing and revenue split. You can
                read more about it
              </Typography>{' '}
              <ExternalLink
                label="here"
                textColor="P-80"
                textVariant="span"
                textSize="medium"
                href="https://help.playbookapp.io/en/articles/13622542-how-to-use-push-notifications-to-upsell-to-exclusive"
              />
            </div>
          }
        />
        {hasExclusiveSub ? (
          <Promote {...p} />
        ) : (
          <Manage
            freeTrial={freeTrial}
            promotions={promotions}
            landingPage={landingPage}
          />
        )}
      </div>
    </SideModal>
  );
};

type PriceProps = {
  monthly?: string;
  annual?: string;
};

const Price = ({ monthly, annual }: PriceProps) => (
  <Row>
    {monthly && (
      <Col>
        <Typography variant="body" size="large">
          Price per month
        </Typography>
        <Typography variant="body" size="large">
          {monthly}
        </Typography>
      </Col>
    )}
    {annual && (
      <Col>
        <Typography variant="body" size="large">
          Price per year
        </Typography>
        <Typography variant="body" size="large">
          {annual}
        </Typography>
      </Col>
    )}
  </Row>
);

type ManageProps = Pick<
  EditPlatformBundleVM['platformBundleModal'],
  'freeTrial' | 'promotions' | 'landingPage'
>;

const Manage = ({ freeTrial, promotions, landingPage }: ManageProps) => (
  <>
    <div className={freeTrialSection}>
      <div className={rowSection}>
        <Switch
          checked={freeTrial.enabled}
          onChange={(e) => freeTrial.onChange(e.target.checked)}
          color="primary"
        />
        <Typography variant="body" size="large">
          7-day free trial
        </Typography>
      </div>
      <Typography variant="body" size="medium" fontWeight="normal" color="N-80">
        Allow members to try subscription for 7 days before being charged.
      </Typography>
    </div>
    <Divider />
    <div className={rowSection}>
      <div className={rowContent}>
        <Typography variant="body" size="large">
          Promotions
        </Typography>
        <Typography
          variant="body"
          size="medium"
          fontWeight="normal"
          color="N-80"
        >
          Setup discounts for your offer
        </Typography>
      </div>
      <div className={rowActions}>
        <Button
          onClick={promotions.createDiscount}
          variant="filled"
          color="primaryTonal"
          size="medium"
        >
          Create discount
        </Button>
      </div>
    </div>
    <Divider />
    <div className={rowSection}>
      <div className={rowContent}>
        <Typography variant="body" size="large">
          Landing page
        </Typography>
        <Typography
          variant="body"
          size="medium"
          fontWeight="normal"
          color="N-80"
        >
          Edit your automated channel page &amp; share links
        </Typography>
      </div>
      <div className={rowActions}>
        <Button
          onClick={landingPage.openLinksModal}
          variant="text"
          size="medium"
          color="neutral"
        >
          <FontAwesomeIcon icon={faShareNodes} />
        </Button>
        <Button
          onClick={landingPage.edit}
          variant="filled"
          color="primaryTonal"
          size="medium"
        >
          Edit
        </Button>
      </div>
    </div>
  </>
);

const Promote = ({
  cta,
  message,
  sentOn,
  count,
}: Pick<
  EditPlatformBundleVM['platformBundleModal'],
  'cta' | 'message' | 'sentOn' | 'count'
>) => (
  <div className={promoteWrapper}>
    <Row>
      <Col>
        <Typography variant="body" size="large">
          Promote your Exclusive Subscription to Platform users
        </Typography>
        <Typography variant="body" size="medium" fontWeight="normal">
          Use the link below and/or the Push notification feature to encourage
          your current Platform users to either switch to your Exclusive
          subscription, or add it on top of their Platform subscription.
        </Typography>
      </Col>
    </Row>
    <div className={limitWrapper}>
      {sentOn && (
        <div className={messageWrapper}>
          <Typography variant="body" size="medium" fontWeight="normal">
            Push notification has been sent on {`${sentOn}`}
          </Typography>
          <Typography>{message}</Typography>
        </div>
      )}
      <Typography variant="body" size="small" fontWeight="normal" color="N-60">
        Push notification limit {count}/2
      </Typography>
    </div>
    <div className={ctaWrapper}>
      {cta.map(({ label, onClick, disabled }) => (
        <Button
          key={label}
          onClick={onClick}
          variant="text"
          size="medium"
          disabled={disabled}
        >
          {label}
        </Button>
      ))}
    </div>
  </div>
);
